import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Col,
  Progress,
  Row,
  Statistic,
  Table,
  Tag,
  Space,
  Button,
  Input,
  message,
  Tooltip,
  Alert,
} from 'antd';
import {
  SyncOutlined,
  ApiOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';

const REFRESH_MS = 30000;

const LEVEL_COLORS = {
  green: '#52c41a',
  yellow: '#faad14',
  orange: '#fa8c16',
  red: '#ff4d4f',
};

const LEVEL_LABELS = {
  green: '正常',
  yellow: '预警',
  orange: '暂停',
  red: '硬停',
};

function levelKey(level) {
  return String(level || 'green').toLowerCase();
}

function pctColor(pct, thresholds) {
  const v = Number(pct) || 0;
  const t = thresholds || { warn: 60, pause: 80, hard_stop: 95 };
  if (v >= t.hard_stop) return LEVEL_COLORS.red;
  if (v >= t.pause) return LEVEL_COLORS.orange;
  if (v >= t.warn) return LEVEL_COLORS.yellow;
  return LEVEL_COLORS.green;
}

function UsageBar({ label, pct, thresholds }) {
  const value = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
        <span>{label}</span>
        <span>{value.toFixed(1)}%</span>
      </div>
      <Progress
        percent={value}
        showInfo={false}
        strokeColor={pctColor(value, thresholds)}
        size="small"
      />
    </div>
  );
}

function UsageBarBlock({ usage, thresholds }) {
  const data = usage || { peak_pct: 0, call_pct: 0, cpu_pct: 0, time_pct: 0 };
  return (
    <>
      <UsageBar label="峰值" pct={data.peak_pct} thresholds={thresholds} />
      <UsageBar label="Call" pct={data.call_pct} thresholds={thresholds} />
      <UsageBar label="CPU" pct={data.cpu_pct} thresholds={thresholds} />
      <UsageBar label="Time" pct={data.time_pct} thresholds={thresholds} />
      {data.updated_at ? (
        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
          更新: {new Date(data.updated_at).toLocaleString()}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>无 API 响应记录，显示 0</div>
      )}
    </>
  );
}

const FbUsageMonitor = () => {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [accountDetail, setAccountDetail] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/fb-usage/overview');
      setOverview(res.data);
    } catch (err) {
      message.error(err.response?.data?.detail || '获取用量概览失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAccountDetail = useCallback(async (actId) => {
    const id = (actId || '').trim();
    if (!id) {
      setAccountDetail(null);
      return;
    }
    setAccountLoading(true);
    try {
      const res = await api.get('/fb-usage/current', {
        params: { facebook_account_id: id },
      });
      setAccountDetail(res.data);
    } catch (err) {
      message.error(err.response?.data?.detail || '获取账户用量失败');
    } finally {
      setAccountLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    const timer = setInterval(fetchOverview, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchOverview]);

  const thresholds = overview?.thresholds || { warn: 60, pause: 80, hard_stop: 95 };

  const appColumns = [
    {
      title: '应用',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <strong>{name}</strong>
          <span style={{ fontSize: 12, color: '#888' }}>App ID: {row.app_id}</span>
        </Space>
      ),
    },
    {
      title: '状态',
      key: 'gate',
      width: 120,
      render: (_, row) => {
        const level = levelKey(row.gate?.level);
        const blocked = row.gate?.app_blocked;
        return (
          <Space direction="vertical" size={0}>
            <Tag color={LEVEL_COLORS[level] || 'default'}>
              {LEVEL_LABELS[level] || level}
            </Tag>
            {blocked ? (
              <Tag icon={<PauseCircleOutlined />} color="error">App 已限流</Tag>
            ) : (
              <Tag icon={<CheckCircleOutlined />} color="success">可创建</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'App 用量 (X-App-Usage)',
      key: 'usage',
      width: 280,
      render: (_, row) => (
        <UsageBarBlock usage={row.usage} thresholds={thresholds} />
      ),
    },
    {
      title: '峰值',
      key: 'peak',
      width: 80,
      render: (_, row) => `${(row.gate?.peak_pct || 0).toFixed(1)}%`,
    },
    {
      title: '开发者数',
      dataIndex: 'developer_count',
      key: 'developer_count',
      width: 90,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
    },
  ];

  const pausedColumns = [
    { title: '广告账户', dataIndex: 'facebook_account_id', key: 'act' },
    { title: 'App ID', dataIndex: 'application_id', key: 'app' },
    { title: '用例', dataIndex: 'use_case', key: 'use_case' },
    {
      title: '峰值',
      dataIndex: 'peak_pct',
      key: 'peak_pct',
      render: (v) => `${(v || 0).toFixed(1)}%`,
    },
    { title: '原因', dataIndex: 'pause_reason', key: 'reason', ellipsis: true },
    { title: '任务', dataIndex: 'paused_task_id', key: 'task' },
    {
      title: '暂停时间',
      dataIndex: 'paused_at',
      key: 'paused_at',
      render: (v) => (v ? new Date(v).toLocaleString() : '-'),
    },
  ];

  const accountUsage = accountDetail?.usage || {};

  return (
    <div>
      <PageHeader
        title="Facebook API 用量"
        subTitle="监测 X-App-Usage / X-Ad-Account-Usage / X-Business-Use-Case-Usage"
        extra={
          <Button icon={<SyncOutlined spin={loading} />} onClick={fetchOverview}>
            刷新
          </Button>
        }
      />

      {overview && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card>
                <Statistic title="应用总数" value={overview.app_count} prefix={<ApiOutlined />} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="App 限流中"
                  value={overview.app_blocked_count}
                  valueStyle={{ color: overview.app_blocked_count > 0 ? '#ff4d4f' : undefined }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="本小时暂停记录" value={overview.paused_count} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="App 自动轮换"
                  value={overview.rotation_enabled ? '已启用' : '已关闭'}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
          </Row>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`阈值：预警 ≥${thresholds.warn}% · 暂停 ≥${thresholds.pause}% · 硬停 ≥${thresholds.hard_stop}%`}
            description="数据仅来自 Graph API 响应头写入的数据库快照；未调用过 API 的 App/账户显示 0。App 级限流会影响同一 App 下所有账户。"
          />

          <Card title="App 用量概览" style={{ marginBottom: 16 }}>
            <Table
              rowKey="application_id"
              loading={loading}
              columns={appColumns}
              dataSource={overview.apps || []}
              pagination={false}
              size="middle"
            />
          </Card>

          {overview.paused_count > 0 && (
            <Card title={`本小时暂停 (${overview.window_hour})`} style={{ marginBottom: 16 }}>
              <Table
                rowKey={(r) => `${r.facebook_account_id}-${r.application_id}-${r.paused_at}`}
                columns={pausedColumns}
                dataSource={overview.paused || []}
                pagination={false}
                size="small"
              />
            </Card>
          )}
        </>
      )}

      <Card title="单账户用量查询">
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="act_xxx 或数字账户 ID"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            style={{ width: 280 }}
            onPressEnter={() => fetchAccountDetail(accountId)}
          />
          <Button loading={accountLoading} onClick={() => fetchAccountDetail(accountId)}>
            查询
          </Button>
        </Space>

        {accountDetail && (
          <Row gutter={16}>
            <Col span={8}>
              <Card size="small" title="网关决策">
                <Space direction="vertical">
                  <Tag color={LEVEL_COLORS[levelKey(accountDetail.gate?.level)] || 'default'}>
                    {LEVEL_LABELS[levelKey(accountDetail.gate?.level)] || accountDetail.gate?.level}
                  </Tag>
                  {accountDetail.gate?.app_blocked && <Tag color="error">App 已限流</Tag>}
                  {accountDetail.gate?.account_only_blocked && (
                    <Tag color="warning">仅账户限流</Tag>
                  )}
                  <Tooltip title={accountDetail.gate?.message}>
                    <span style={{ fontSize: 12 }}>{accountDetail.gate?.message}</span>
                  </Tooltip>
                </Space>
              </Card>
            </Col>
            <Col span={5}>
              <Card size="small" title="X-Ad-Account-Usage">
                <UsageBarBlock usage={accountUsage.ad_account} thresholds={thresholds} />
              </Card>
            </Col>
            <Col span={5}>
              <Card size="small" title="X-Business-Use-Case-Usage">
                <UsageBarBlock usage={accountUsage.buc} thresholds={thresholds} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" title="批量参数">
                <div>batch_size: {accountDetail.gate?.batch_size}</div>
                <div>cooldown: {accountDetail.gate?.batch_cooldown}s</div>
                <div>can_create: {accountDetail.gate?.can_create ? '是' : '否'}</div>
              </Card>
            </Col>
          </Row>
        )}
      </Card>
    </div>
  );
};

export default FbUsageMonitor;
